import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styles from './countdown-indicator.module.css';

export default function indicatorFactory({ console }) {
  return CountdownIndicator;

  /**
   * @todo Resolve strokeWidth dependency on
   * @todo Reduce complexity
   *
   * @param props
   * @return {JSX.Element}
   * @see {@link https://codesandbox.io/s/sharp-ully-5nb98d?file=/src/Path.js reference}
   */
  function CountdownIndicator(props) {
    const {
      timeout,
      onComplete,
      width,
      height,
      borderRadius = 0,
      strokeWidth = 6,
      strokeGap = 0,
      originalWidth,
    } = props;
    /**
     * @type {React.MutableRefObject<SVGPathElement>}
     */
    const ref = useRef();

    useEffect(() => {
      if (!ref.current) {
        return;
      }
      const dashLength = ref.current.getTotalLength();

      ref.current.style.setProperty('--stroke-dash-length', dashLength);
      ref.current.style.setProperty('animation-duration', `${timeout}s`);
      ref.current.style.setProperty(
        'animation-name',
        styles.indicatorAnimation,
      );
    }, [onComplete, timeout, width, height, strokeWidth]);

    const onAnimationEnd = useCallback(
      (animationEvent) => {
        if (animationEvent.animationName !== styles.indicatorAnimation) {
          return;
        }
        if (animationEvent.elapsedTime < 1) {
          console.log(
            'too low time elapsed, fake trigger !?',
            animationEvent.elapsedTime,
          );
          return;
        }

        animationEvent.preventDefault();
        animationEvent.stopPropagation();
        console.log(
          `animation ${animationEvent.animationName} done in ${animationEvent.elapsedTime} s.`,
        );
        onComplete(animationEvent);
      },
      [onComplete],
    );

    const pathD = useMemo(() => {
      const radius = Math.max(0, Math.min(borderRadius, width / 2, height / 2));
      const vLength = Math.max(0, height - 2 * radius);
      const stroke = strokeGap > -1 ? strokeWidth / 2 : strokeWidth;
      const borderWidth = stroke + strokeGap;

      return `
        M ${(width + borderWidth) / 2},${borderWidth / 2}
        h ${width / 2 - radius}
        a ${radius},${radius} 0 0 1 ${radius},${radius}
        v ${vLength}
        a ${radius},${radius} 0 0 1 -${radius},${radius}
        h -${width - 2 * radius}
        a ${radius},${radius} 0 0 1 -${radius},-${radius}
        v ${-vLength}
        a ${radius},${radius} 0 0 1 ${radius},-${radius}
        z`;
    }, [borderRadius, width, height, strokeWidth, strokeGap]);

    if (!timeout || isNaN(timeout)) {
      console.warn('unexpected timeout value', timeout);
      return null;
    }

    return (
      <svg
        data-role="countdown-indicator"
        className={styles.indicatorContainer}
        viewBox={`0 0 ${width + strokeWidth} ${height + strokeWidth}`}
      >
        <path
          ref={ref}
          strokeWidth={strokeWidth}
          d={pathD}
          onAnimationEnd={onAnimationEnd}
          className={styles.indicator}
        />
      </svg>
    );
  }
}
